import React, {useState, useEffect} from "react";
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import Button from "./ds/Button";
import Date from "./ds/Date";
import Select from "./ds/Select";
import Switch from "./ds/Switch";
import Time from "./Time";
import {categoryService} from "../container";
import config from "../config";
import {postState} from "../constants";
import {useGlobalState} from '../state';

export default function PublishingOptions({
  post,
  saving,
  publishing,
  onChange,
  onSave,
  onPublish,
  onUnpublish,
  onSchedule,
  onUnschedule,
}) {
  const {media} = useGlobalState();
  const [categories, setCategories] = useState([]);
  const [scheduling, setScheduling] = useState(false);
  const [scheduledAt, setScheduledAt] = useState(dayjs().add(1, 'day'));
  const [customPublishedAt, setCustomPublishedAt] = useState(false);
  const [publishedAt, setPublishedAt] = useState(dayjs());
  const [hasErrorShown, setHasErrorShown] = useState(false);

  const schedulingDate = scheduledAt.format('DD/MM/YYYY');
  const schedulingTime = scheduledAt.format('hh:mm A');

  const publishedAtDate = publishedAt.format('DD/MM/YYYY');
  const publishedAtTime = publishedAt.format('hh:mm A');

  useEffect(() => {
    (async () => {
      try {
        const categories = await categoryService.search({
          media_id: media.id,
          state: 'active',
        });

        setCategories(categories.map((category) => ({
          id: category.id,
          title: category.cms_title ? category.cms_title : category.title,
        })));
      } catch (err) {
        toast.error('Ocurrió un error al cargar las secciones.');
        console.error(err);
      }
    })();
  }, []);

  useEffect(() => {
    if (post.state === postState.scheduled && post.published_at) {
      setScheduledAt(dayjs(post.published_at));
      setScheduling(true);
    }

    if (post.state === postState.draft && post.published_at) {
      setPublishedAt(dayjs(post.published_at));
      setCustomPublishedAt(true);
    }
  }, [post]);

  useEffect(() => {
    if (scheduling && customPublishedAt) {
      setCustomPublishedAt(false);
    }
  }, [scheduling]);

  useEffect(() => {
    if (customPublishedAt && scheduling) {
      setScheduling(false);
    }
  }, [customPublishedAt]);

  function toggleCustomDateTime() {
    setScheduling((scheduling) => !scheduling);
  }

  const handleSchedulingDateChange = (date) => {
    const newScheduledAt = dayjs(`${date} ${schedulingTime}`, 'DD/MM/YYYY hh:mm A');
    const now = dayjs();
    if (newScheduledAt.isAfter(now, 'day') || newScheduledAt.isSame(now, 'day')) {
      setScheduledAt(newScheduledAt);
      setHasErrorShown(false);
    } else {
      if (!hasErrorShown) {
        toast.error('La fecha programada debe ser mayor a la fecha y hora actual.');
        setHasErrorShown(true);
      }
    }
  };

  const handleSchedulingTimeChange = (time) => {
    const newScheduledAt = dayjs(`${schedulingDate} ${time}`, 'DD/MM/YYYY hh:mm A');
    setScheduledAt(newScheduledAt);
  };

  const handlePublishedAtDateChange = (date) => {
    const newPublishedAt = dayjs(`${date} ${publishedAtTime}`, 'DD/MM/YYYY hh:mm A');
    setPublishedAt(newPublishedAt);
  };

  const handlePublishedAtTimeChange = (time) => {
    const newPublishedAt = dayjs(`${publishedAtDate} ${time}`, 'DD/MM/YYYY hh:mm A');
    setPublishedAt(newPublishedAt);
  };

  const canPostBeSaved = post.state === postState.draft
    && post.title.length >= config.title.minLength
    && post.subtitle.length >= config.subtitle.minLength
    && post.category
    && (post.seo_title.length === 0 || (post.seo_title.length >= config.seoTitle.minLength))
  const canPostBePublished = post.state === postState.draft && !post.isTouched;
  const canPostBeUnpublished = post.state === postState.published;
  const canPostBeScheduled = post.state === postState.draft && !post.isTouched && scheduledAt.isAfter(dayjs().add(10, 'minute'));
  const canPostBeUnscheduled = post.state === postState.scheduled;

  return (
    <div id="publishing-options">
      <div>
        <h2 className="t-heading-m">Opciones de publicación</h2>

        <div className="fields">
          <Select
            placeholder="Elige una sección"
            value={post.category?.id}
            onChange={(e) => onChange({category: categories.find((category) => category.id === e.target.value)})}
          >
            {categories.map((category) => (
              <option key={category.id} value={category.id}>{category.title}</option>
            ))}
          </Select>

          <div className="field">
            <h3 className="t-heading-s">Para tráfico de Discovery</h3>

            <Switch
              enabled={post.target === 'discovery'}
              onclick={() => onChange({ target: post.target === 'discovery' ? null : 'discovery' })}
            />
          </div>

          <div className="date-time">
            <div>
              <h3 className="t-heading-s">Programar publicación</h3>

              <Switch
                enabled={scheduling}
                onclick={toggleCustomDateTime}
              />
            </div>

            <div className={`wrapper ${scheduling ? '' : 'disabled'}`}>
              <Date
                value={schedulingDate}
                onChange={handleSchedulingDateChange}
                placeholder="--/--/----"
              />
              <Time
                value={schedulingTime}
                onChange={handleSchedulingTimeChange}
              />
            </div>

            {scheduling && (
              <div className="description">
                <div><b>Hora local</b>: {scheduledAt.format('HH:mm (Z) DD/MM/YYYY')}</div>
                <div><b>Hora UTC</b>: {scheduledAt.utc().format('HH:mm (Z) DD/MM/YYYY')}</div>
              </div>
            )}
          </div>

        </div>

        <div className="buttons">
          {post.state === postState.draft && (
            <>
              <Button
                type="secondary"
                label={post.isNew ? 'Crear' : 'Guardar'}
                loading={saving}
                onclick={() => onSave(post)}
                disabled={!canPostBeSaved}
              />
              {!post.isNew &&
              scheduling ? (
                <Button
                  label="Programar"
                  loading={publishing}
                  disabled={!canPostBeScheduled}
                  onclick={() => onSchedule(post, scheduledAt)}
                />
              ) : (
                <Button
                  label="Publicar"
                  loading={publishing}
                  disabled={!canPostBePublished}
                  onclick={() => onPublish(post, publishedAt)}
                />
              )
              }
            </>
          )}

          {post.state === postState.published && (
            <Button
              label="Despublicar"
              loading={publishing}
              disabled={!canPostBeUnpublished}
              onclick={() => onUnpublish(post)}
            />
          )}

          {post.state === postState.scheduled && (
            <Button
              label="Desprogramar"
              loading={publishing}
              disabled={!canPostBeUnscheduled}
              onclick={() => onUnschedule(post)}
            />
          )}
        </div>
      </div>
    </div>
  )
}
